
import { useState, useEffect } from 'react'
import type { CanvasActions } from '@/types/canvas'

interface UseCanvasKeyboardParams {
  enabled?: boolean
  selectedNodes: string[]
  canvasActions: CanvasActions | null
}

interface UseCanvasKeyboardReturn {
  isSpacePressed: boolean
  isPanning: boolean
  setIsPanning: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * Manages keyboard interactions for the canvas page.
 *
 * Responsibilities:
 * - Space key grab cursor (Figma-style panning)
 * - Browser zoom prevention (ctrl+scroll)
 * - Shift+0 zoom reset, Shift+1 zoom to fit
 */
export function useCanvasKeyboard({
  enabled = true,
  selectedNodes,
  canvasActions,
}: UseCanvasKeyboardParams): UseCanvasKeyboardReturn {
  // Space key cursor state (Figma-style grab cursor)
  const [isSpacePressed, setIsSpacePressed] = useState(false)
  const [isPanning, setIsPanning] = useState(false)

  // Global wheel event capture for ctrl+scroll zoom prevention
  useEffect(() => {
    if (!enabled) return
    const preventBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        // Prevent browser zoom, allow only canvas zoom
        e.preventDefault()
      }
    }

    // Capture phase to intercept ALL wheel events before they reach child elements
    // This ensures ctrl+scroll always triggers canvas zoom, never browser zoom
    document.addEventListener('wheel', preventBrowserZoom, {
      passive: false,  // Required for preventDefault
      capture: true    // Capture phase = catches event at top level
    })

    return () => {
      document.removeEventListener('wheel', preventBrowserZoom, { capture: true })
    }
  }, [enabled])

  // Figma-style keyboard shortcuts
  useEffect(() => {
    if (!enabled) { setIsSpacePressed(false); setIsPanning(false); return }
    const handleKeyDown = (e: KeyboardEvent) => {
      // Space key: Enable grab cursor (only if no nodes selected and not in text input)
      if (e.code === 'Space' && !e.repeat) {
        const target = e.target as HTMLElement
        const isTextInput =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable

        // Only enable grab cursor if: no nodes selected AND not in text input
        if (selectedNodes.length === 0 && !isTextInput) {
          setIsSpacePressed(true)
        }
        return
      }

      // Shift+0: Zoom Reset (%100) - Use event.code for cross-platform compatibility
      if (e.shiftKey && e.code === 'Digit0' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        e.stopPropagation()
        canvasActions?.onZoomReset?.()
        return
      }

      // Shift+1: Zoom to Fit (all nodes) - Use event.code for cross-platform compatibility
      if (e.shiftKey && e.code === 'Digit1' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        e.stopPropagation()
        canvasActions?.onZoomToFit?.()
        return
      }

      // Note: Cmd+=/Cmd+- zoom shortcuts are handled by useShortcuts.ts
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      // Space key released: Disable grab cursor
      if (e.code === 'Space') {
        setIsSpacePressed(false)
        setIsPanning(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [canvasActions, selectedNodes, enabled])

  return {
    isSpacePressed,
    isPanning,
    setIsPanning,
  }
}
